import React, { Component } from 'react';
import {
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Divider
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import * as ETVConstants from '../ETVConstants';

const ALIGNMENT_OPTIONS = [
  { label: ETVConstants.trans("bottom_right"), value: 0 },
  { label: ETVConstants.trans('bottom_left'), value: 1 },
  { label: ETVConstants.trans('top_left'), value: 2 },
  { label: ETVConstants.trans('top_right'), value: 3 },
  { label: ETVConstants.trans('center_center'), value: 4 },
];

class QRCodeOverlay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      qrType: '',
      inputFields: {},
      errorText: '',
      finalUrl: '',
      alignment: 4, // default center_center
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData() {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = `${ETVConstants.getServerBase()}/content_library?action=get_qr_code&content_id=${this.props.content_id}`;

    fetch(url, {
      headers: { 'user_id': reqUserID, 'access_token': accessToken }
    })
      .then(res => res.json())
      .then(
        (result) => {
          const qr = result["qr_code"];
          // if backend returns alignment, pick it; otherwise keep default 4
          const returnedAlignment = Number.isInteger(result["qr_code_alignment"])
            ? result["qr_code_alignment"]
            : this.state.alignment;

          const type = this.getType(qr);
          const fields = this.prefillInputFields(type, qr);
          this.setState({
            qrType: type,
            inputFields: fields,
            alignment: returnedAlignment
          }, this.constructUrl);
        },
        (error) => {
          console.error("Failed to load data", error);
        }
      );
  }

  getType(qrCode) {
    if (!qrCode) return 'general';
    if (this.props.download_url && qrCode === this.props.download_url) return 'download';
    if (qrCode.includes("wa.me") || qrCode.includes("whatsapp.com")) return "whatsapp";
    if (qrCode.includes("instagram")) return "instagram";
    if (qrCode.includes("linkedin")) return "linkedin";
    if (qrCode.includes("facebook")) return "facebook";
    if (qrCode.includes("twitter") || qrCode.includes("x.com")) return "twitter";
    if (qrCode.includes("youtube")) return "youtube";
    if (qrCode.includes("tiktok")) return "tiktok";
    if (qrCode.includes("telegram")) return "telegram";
    if (qrCode.includes("snapchat")) return "snapchat";
    if (qrCode.includes("pinterest")) return "pinterest";
    if (qrCode.startsWith("sms:")) return "sms";
    if (qrCode.startsWith("BEGIN:VCARD")) return "vcard";
    if (qrCode.startsWith("WIFI:")) return "wifi";
    return "general";
  }

  prefillInputFields(type, qrCode) {
    switch (type) {
      case 'general':
        return { url: qrCode };
      case 'whatsapp': {
        const match = qrCode.match(/wa\.me\/(\+?\d+)/);
        return match ? { phone: '+' + match[1].replace('+', '') } : {};
      }
      case 'sms': {
        const match = qrCode.match(/sms:(\d+)\?body=(.+)/);
        return match ? { phone: match[1], message: decodeURIComponent(match[2]) } : {};
      }
      case 'vcard': {
        const lines = qrCode.split('\n');
        const data = {};
        lines.forEach(line => {
          if (line.startsWith("N:")) {
            const [last, first] = line.substring(2).split(';');
            data.firstName = first;
            data.lastName = last;
          } else if (line.startsWith("FN:")) {
            data.fullName = line.substring(3);
          } else if (line.startsWith("TEL:")) {
            data.phone = line.substring(4);
          } else if (line.startsWith("EMAIL:")) {
            data.email = line.substring(6);
          } else if (line.startsWith("ORG:")) {
            data.org = line.substring(4);
          } else if (line.startsWith("TITLE:")) {
            data.title = line.substring(6);
          }
        });
        return data;
      }
      case 'wifi': {
        const match = qrCode.match(/^WIFI:T:(.*);S:(.*);P:(.*);H:(true|false)?;/);
        return match ? {
          encryption: match[1],
          ssid: match[2],
          password: match[3],
          hidden: match[4] === "true"
        } : {};
      }
      default: {
        const parts = qrCode.split('/');
        const lastSegment = parts[parts.length - 1];
        return lastSegment ? { username: lastSegment } : {};
      }
    }
  }

  handleTypeChange = (event) => {
    const type = event.target.value;
    this.setState({
      qrType: type,
      inputFields: {},
      errorText: '',
      finalUrl: ''
    }, this.constructUrl);
  };

  handleAlignmentChange = (event) => {
    const alignment = Number(event.target.value);
    this.setState({ alignment }, () => {
      // re-emit with the same URL, just new alignment
      this.emitFinalUrl(this.state.finalUrl, this.state.alignment);
    });
  };

  handleFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    this.setState(prevState => ({
      inputFields: {
        ...prevState.inputFields,
        [field]: value
      }
    }), this.constructUrl);
  };

  emitFinalUrl(url, alignment) {
    // prefer new prop name if provided, else fallback to existing
    if (typeof this.props.onUrlcodechange === 'function') {
      this.props.onUrlcodechange(url || '', alignment);
    } else if (typeof this.props.onQRUrlChange === 'function') {
      this.props.onQRUrlChange(url || '', alignment);
    }
  }

  constructUrl = () => {
    const { qrType, inputFields, alignment } = this.state;
    let url = '';
    let error = '';

    switch (qrType) {
      case 'download':
        url = this.props.download_url;
        break;
      case 'general':
        url = inputFields.url || '';
        if (url && !/^https?:\/\//.test(url)) {
          error = 'URL must start with http:// or https://';
        }
        break;
      case 'whatsapp':
        if (inputFields.phone) {
          const phone = inputFields.phone.replace(/\D/g, '');
          if (phone.length < 10) {
            error = 'Please enter a valid international phone number (with country code).';
          } else {
            url = `https://wa.me/${phone}`;
          }
        } else {
          error = 'Please enter a phone number.';
        }
        break;
      case 'sms':
        if (inputFields.phone && inputFields.message) {
          const phone = inputFields.phone.replace(/\D/g, '');
          url = `sms:${phone}?body=${encodeURIComponent(inputFields.message)}`;
        } else {
          error = 'Please enter both phone number and message.';
        }
        break;
      case 'youtube':
        if (inputFields.username) {
          if (inputFields.username.startsWith('playlist:')) {
            url = `https://www.youtube.com/playlist?list=${inputFields.username.replace('playlist:', '')}`;
          } else if (inputFields.username.startsWith('shorts:')) {
            url = `https://www.youtube.com/shorts/${inputFields.username.replace('shorts:', '')}`;
          } else {
            url = `https://youtube.com/watch?v=${inputFields.username}`;
          }
        } else {
          error = 'Please enter a YouTube video ID, playlist ID (prefix with "playlist:"), or shorts ID (prefix with "shorts:").';
        }
        break;
      case 'tiktok':
        if (inputFields.username) {
          if (inputFields.username.startsWith('video:')) {
            url = `https://www.tiktok.com/@placeholder/video/${inputFields.username.replace('video:', '')}`;
          } else {
            url = `https://www.tiktok.com/@${inputFields.username}`;
          }
        } else {
          error = 'Please enter a TikTok username or use "video:ID" for direct video.';
        }
        break;
      case 'wifi': {
        const { encryption = 'WPA', ssid, password, hidden } = inputFields;
        if (!ssid || !password) {
          error = 'Please enter both SSID and password.';
          break;
        }
        url = `WIFI:T:${encryption};S:${ssid};P:${password};${hidden ? 'H:true;' : ''};`;
        break;
      }
      case 'vcard': {
        const { firstName, lastName, phone, email, org, title } = inputFields;
        if (!firstName || !lastName || !phone) {
          error = 'First name, last name, and phone are required for a vCard.';
          break;
        }
        url = [
          "BEGIN:VCARD",
          "VERSION:3.0",
          `N:${lastName};${firstName}`,
          `FN:${firstName} ${lastName}`,
          org ? `ORG:${org}` : '',
          title ? `TITLE:${title}` : '',
          `TEL:${phone}`,
          email ? `EMAIL:${email}` : '',
          "END:VCARD"
        ].filter(Boolean).join('\n');
        break;
      }
      default: {
        if (inputFields.username) {
          const baseUrls = {
            instagram: 'https://instagram.com/',
            facebook: 'https://facebook.com/',
            twitter: 'https://twitter.com/',
            linkedin: 'https://linkedin.com/in/',
            telegram: 'https://t.me/',
            snapchat: 'https://snapchat.com/add/',
            pinterest: 'https://pinterest.com/'
          };
          const base = baseUrls[qrType];
          if (base) url = `${base}${inputFields.username}`;
          else error = 'Unsupported platform.';
        } else {
          error = 'Please enter a username or ID.';
        }
      }
    }

    this.setState({ finalUrl: url, errorText: error }, () => {
      this.emitFinalUrl(error ? null : url, alignment);
    });
  };

  renderPlatformIcon() {
    const { qrType } = this.state;
    const iconMap = {
      whatsapp: 'whatsapp.png',
      instagram: 'instagram.png',
      facebook: 'facebook.png',
      twitter: 'twitter.png',
      linkedin: 'linkedin.png',
      youtube: 'youtube.png',
      tiktok: 'tiktok.png',
      telegram: 'telegram.png',
      snapchat: 'snapchat.png',
      pinterest: 'pinterest.png'
    };
    const iconFile = iconMap[qrType];
    if (!iconFile) return null;

    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Box
          component="img"
          src={`/image_assets/social/${iconFile}`}
          alt={qrType}
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: 3,
            padding: 1
          }}
        />
      </Box>
    );
  }

  renderInputFields() {
    const { qrType, inputFields } = this.state;

    if (qrType === 'general') {
      return (
        <TextField
          size="small"
          fullWidth
          disabled={this.props.disabled}
          label="Enter URL"
          placeholder="https://www.google.com"
          value={inputFields.url || ''}
          onChange={this.handleFieldChange('url')}
        />
      );
    }

    if (qrType === 'whatsapp') {
      return (
        <TextField
          size="small"
          fullWidth
          disabled={this.props.disabled}
          label="Phone Number (with country code)"
          placeholder="e.g., +6591234567"
          value={inputFields.phone || ''}
          onChange={this.handleFieldChange('phone')}
        />
      );
    }

    if (qrType === 'vcard') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="First Name" value={inputFields.firstName || ''} onChange={this.handleFieldChange('firstName')} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Last Name" value={inputFields.lastName || ''} onChange={this.handleFieldChange('lastName')} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Phone" value={inputFields.phone || ''} onChange={this.handleFieldChange('phone')} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Email" value={inputFields.email || ''} onChange={this.handleFieldChange('email')} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Organization" value={inputFields.org || ''} onChange={this.handleFieldChange('org')} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Job Title" value={inputFields.title || ''} onChange={this.handleFieldChange('title')} />
          </Grid>
        </Grid>
      );
    }

    if (qrType === 'wifi') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField size="small" fullWidth label="SSID" value={inputFields.ssid || ''} onChange={this.handleFieldChange('ssid')} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Password" type="password" value={inputFields.password || ''} onChange={this.handleFieldChange('password')} />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Encryption</InputLabel>
              <Select value={inputFields.encryption || 'WPA'} onChange={this.handleFieldChange('encryption')}>
                <MenuItem value="WPA">WPA/WPA2</MenuItem>
                <MenuItem value="WEP">WEP</MenuItem>
                <MenuItem value="nopass">None</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <input
                  type="checkbox"
                  checked={inputFields.hidden || false}
                  onChange={(e) => this.handleFieldChange('hidden')({ target: { value: e.target.checked } })}
                />
              }
              label="Hidden Network"
            />
          </Grid>
        </Grid>
      );
    }

    if (qrType === 'sms') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              size="small"
              fullWidth
              disabled={this.props.disabled}
              label="Phone Number"
              placeholder="e.g., +6591234567"
              value={inputFields.phone || ''}
              onChange={this.handleFieldChange('phone')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              fullWidth
              disabled={this.props.disabled}
              label="Message"
              placeholder="message"
              value={inputFields.message || ''}
              onChange={this.handleFieldChange('message')}
            />
          </Grid>
        </Grid>
      );
    }

    if (qrType === 'download') return null;

    const label = qrType === 'youtube'
      ? 'Video ID / playlist:ID / shorts:ID'
      : qrType === 'tiktok'
        ? 'Username or video:ID'
        : 'Username';

    return (
      <TextField
        size="small"
        fullWidth
        disabled={this.props.disabled}
        label={label}
        placeholder={label.toLowerCase()}
        value={inputFields.username || ''}
        onChange={this.handleFieldChange('username')}
      />
    );
  }

  render() {
    const { qrType, errorText, finalUrl, alignment } = this.state;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2">
          {ETVConstants.trans("qr_code_overlay_explanation")}
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* QR Code Type */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="qr-type-label">{ETVConstants.trans("qr_code_type")}</InputLabel>
          <Select
            labelId="qr-type-label"
            value={qrType}
            disabled={this.props.disabled}
            label="QR Code Type"
            onChange={this.handleTypeChange}
          >
            {this.props.download_url && (
              <MenuItem value="download">Download this file</MenuItem>
            )}
            <MenuItem value="general">General URL</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
            <MenuItem value="instagram">Instagram</MenuItem>
            <MenuItem value="facebook">Facebook</MenuItem>
            <MenuItem value="twitter">Twitter</MenuItem>
            <MenuItem value="linkedin">LinkedIn</MenuItem>
            <MenuItem value="youtube">YouTube</MenuItem>
            <MenuItem value="tiktok">TikTok</MenuItem>
            <MenuItem value="telegram">Telegram</MenuItem>
            <MenuItem value="snapchat">Snapchat</MenuItem>
            <MenuItem value="pinterest">Pinterest</MenuItem>
            <MenuItem value="vcard">vCard Contact</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="wifi">WiFi Network</MenuItem>
          </Select>
        </FormControl>

        {/* Dynamic Inputs */}
        {this.renderInputFields()}

        {/* Alignment Selector */}
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel id="qr-align-label">URL Code Alignment</InputLabel>
          <Select
            labelId="qr-align-label"
            value={alignment}
            disabled={this.props.disabled}
            label="URL Code Alignment"
            onChange={this.handleAlignmentChange}
          >
            {ALIGNMENT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Errors */}
        {errorText && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>{errorText}</Typography>
        )}

        {/* Icon */}
        {this.renderPlatformIcon()}

        {/* Preview */}
        {finalUrl && !errorText && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <QRCodeCanvas value={finalUrl} size={200} />
          </Box>
        )}
      </Box>
    );
  }
}

export default QRCodeOverlay;
